import {
  ChangeDetectionStrategy,
  Component,
  Input,
  signal,
  WritableSignal,
  inject,
  effect,
  computed,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterLink, RouterLinkActive, Router } from "@angular/router";
import { MenuItem } from "../../features/platform/services/ui-config-api.service";
import { PermissionService } from "../../core/services/permission.service";
import { AuthService } from "../../core/services/auth.service";
import { MenuFilterService } from "../../core/services/menu-filter.service";
import { PlanService } from "../../core/services/plan.service";

@Component({
  standalone: true,
  selector: "app-dynamic-menu",
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <ng-container *ngFor="let menu of filteredMenus(); let i = index">
      <!-- Case 1: Parent menu without route (section header with children) -->
      <div
        *ngIf="!menu.route && menu.children && menu.children.length > 0"
        class="mb-2"
      >
        <!-- Section header (only show if not collapsed) -->
        <h3
          *ngIf="!isCollapsed"
          class="px-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2"
        >
          {{ menu.label }}
        </h3>
        <!-- Render children with proper indentation -->
        <div class="space-y-1" [ngClass]="getIndentationClass()">
          <app-dynamic-menu
            [menus]="menu.children"
            [isCollapsed]="isCollapsed"
            [level]="level + 1"
          ></app-dynamic-menu>
        </div>
      </div>

      <!-- Case 2: Menu item with route and possibly children -->
      <div *ngIf="menu.route" class="space-y-1">
        <!-- Parent link with expand/collapse indicator if it has children -->
        <div class="flex items-center">
          <a
            [routerLink]="normalizeRoute(menu.route)"
            class="flex items-center rounded-lg hover:bg-slate-100/80 dark:hover:bg-slate-800/50 transition-colors flex-1 cursor-pointer"
            [ngClass]="getLinkClasses()"
            [attr.title]="isCollapsed ? menu.label : null"
          >
            <svg
              *ngIf="menu.icon || !menu.icon"
              class="h-5 w-5 flex-shrink-0"
              [ngClass]="{ 'mx-auto': isCollapsed }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                [attr.d]="menu.icon || getDefaultIcon(menu.label)"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
              />
            </svg>
            <span *ngIf="!isCollapsed" class="font-medium truncate">{{
              menu.label
            }}</span>
          </a>
          <!-- Expand/collapse indicator for menus with children -->
          <button
            *ngIf="menu.children && menu.children.length > 0 && !isCollapsed"
            (click)="toggleExpanded(i)"
            class="ml-1 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            [attr.aria-expanded]="isExpanded(i)"
          >
            <svg
              class="h-4 w-4 transition-transform"
              [ngClass]="{ 'rotate-90': isExpanded(i) }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
        <!-- Render children if they exist, sidebar is not collapsed, and menu is expanded -->
        <div
          *ngIf="
            menu.children &&
            menu.children.length > 0 &&
            !isCollapsed &&
            isExpanded(i)
          "
          class="space-y-1 transition-all duration-200"
          [ngClass]="getIndentationClass()"
        >
          <app-dynamic-menu
            [menus]="menu.children"
            [isCollapsed]="false"
            [level]="level + 1"
          ></app-dynamic-menu>
        </div>
      </div>

      <!-- Case 3: Menu item without route but with children (should be handled by Case 1, but keep for safety) -->
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicMenuComponent {
  @Input() menus: MenuItem[] = [];
  @Input() isCollapsed: boolean = false;
  @Input() level: number = 0;

  private permissionService = inject(PermissionService);
  private authService = inject(AuthService);
  private menuFilter = inject(MenuFilterService);
  private planService = inject(PlanService);
  private router = inject(Router);
  private expandedMenus: WritableSignal<Set<number>> = signal(new Set());
  private collapsedMenus: WritableSignal<Set<number>> = signal(new Set());

  // Filter menus based on permissions, roles, and plan
  // Backend already filters, but we do client-side filtering as fallback/refinement
  filteredMenus = computed(() => {
    const userRoles = this.authService.roles();
    const userPermissions = this.authService.permissions();
    const tenantPlan = this.planService.currentPlan();

    // Use MenuFilterService for consistent filtering logic
    return this.menuFilter.filterMenus(
      this.menus,
      userRoles,
      tenantPlan,
      userPermissions,
    );
  });

  constructor() {
    // React to permission, role, and plan changes
    effect(() => {
      this.authService.permissions();
      this.authService.roles();
      this.planService.currentPlan();
      // Trigger recomputation of filteredMenus
      this.filteredMenus();
    });
  }

  /**
   * Get default icon for menu items that don't have icons
   */
  getDefaultIcon(menuLabel: string): string {
    const label = menuLabel.toLowerCase();

    // Debug logging to help troubleshoot icon matching
    console.log(`🔍 Checking icon for menu: "${menuLabel}" -> "${label}"`);

    // Icon mappings based on common menu names
    const iconMap: Record<string, string> = {
      // Dashboard/Home
      dashboard:
        "M3 7h13V3H3v4zM3 21h13v-4H3v4zM17 11h4v4h-4v-4zM17 19h4v-4h-4v4zM3 11h7V7H3v4zM10 21h4v-4h-4v4z",
      home: "M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.5z",

      // Users/People
      users:
        "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
      user: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
      people:
        "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
      profile:
        "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
      team: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
      staff:
        "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",

      // Tasks/Projects
      tasks: "M9 5h6v2H9zM6 9h12v2H6zM6 13h12v2H6zM6 17h6v2H6z",
      projects: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
      todo: "M9 5h6v2H9zM6 9h12v2H6zM6 13h12v2H6zM6 17h6v2H6z",

      // Calendar/Scheduling
      calendar:
        "M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z",
      schedule:
        "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z",

      // Reports/Analytics
      reports: "M4 9h4v11H4zM10 4h4v16h-4zM16 14h4v6h-4z",
      analytics: "M4 9h4v11H4zM10 4h4v16h-4zM16 14h4v6h-4z",
      charts: "M4 9h4v11H4zM10 4h4v16h-4zM16 14h4v6h-4z",

      // Settings/Configuration
      settings:
        "M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z",
      setting:
        "M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z",
      config:
        "M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z",
      configuration:
        "M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z",

      // Help/Support
      help: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z",
      support:
        "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z",

      // Files/Drive
      files:
        "M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 18H6v-2h7v2zm3-4H6v-2h10v2zm0-4H6V9h10v2z",
      drive:
        "M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 18H6v-2h7v2zm3-4H6v-2h10v2zm0-4H6V9h10v2z",

      // Communication
      messenger:
        "M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z",
      chat: "M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z",

      // Monitoring/Analytics
      monitoring: "M4 9h4v11H4zM10 4h4v16h-4zM16 14h4v6h-4z",
      metrics: "M4 9h4v11H4zM10 4h4v16h-4zM16 14h4v6h-4z",

      // CRM
      crm: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",

      // Academic
      academic:
        "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",

      // Attendance
      attendance:
        "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z",

      // Audit
      audit:
        "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z",

      // Gateway
      gateway:
        "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",

      // Platform
      platform:
        "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",

      // AI
      ai: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",

      // Default icon (generic document)
      default:
        "M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 18H6v-2h7v2zm3-4H6v-2h10v2zm0-4H6V9h10v2z",
    };

    // Check for exact matches first
    if (iconMap[label]) {
      console.log(`✅ Found exact match for "${label}"`);
      return iconMap[label];
    }

    // Check for partial matches
    for (const [key, icon] of Object.entries(iconMap)) {
      if (label.includes(key)) {
        console.log(`✅ Found partial match for "${label}" -> "${key}"`);
        return icon;
      }
    }

    // Return default icon if no match found
    console.log(`❌ No match found for "${label}", using default icon`);
    return iconMap["default"];
  }

  isExpanded(index: number): boolean {
    // If explicitly collapsed, return false
    if (this.collapsedMenus().has(index)) {
      return false;
    }
    // If explicitly expanded, return true
    if (this.expandedMenus().has(index)) {
      return true;
    }
    // Default: expand all menus for better visibility
    return true;
  }

  toggleExpanded(index: number): void {
    const expanded = new Set(this.expandedMenus());
    const collapsed = new Set(this.collapsedMenus());

    if (this.isExpanded(index)) {
      // Collapse it
      expanded.delete(index);
      collapsed.add(index);
    } else {
      // Expand it
      collapsed.delete(index);
      expanded.add(index);
    }

    this.expandedMenus.set(expanded);
    this.collapsedMenus.set(collapsed);
  }

  getIndentationClass(): string {
    if (this.level === 0) return "";
    const indentLevels = ["ml-3", "ml-6", "ml-9", "ml-12"];
    return (
      indentLevels[Math.min(this.level - 1, indentLevels.length - 1)] || ""
    );
  }

  getLinkClasses(): string {
    const baseClasses = this.isCollapsed
      ? "justify-center px-0 py-2.5"
      : "gap-3 px-3 py-2.5";

    // Add indentation for nested items
    if (this.level > 0 && !this.isCollapsed) {
      const indentLevels = ["", "ml-3", "ml-6", "ml-9"];
      const indent =
        indentLevels[Math.min(this.level, indentLevels.length - 1)] || "";
      return `${baseClasses} ${indent}`;
    }

    return baseClasses;
  }

  /**
   * Normalize backend-provided routes so routerLink behaves consistently.
   *
   * Backend routes are typically absolute (e.g. "/tasks"). If a route is provided
   * without a leading slash (e.g. "tasks"), Angular treats it as relative to the
   * current activated route. That can lead to unexpected navigation.
   */
  normalizeRoute(route?: string): string {
    if (!route) return "/";
    return route.startsWith("/") ? route : `/${route}`;
  }

  // Kept for backward compatibility (older templates used click handlers)
  handleMenuClick(menu: MenuItem): void {
    if (menu?.route) {
      this.router.navigateByUrl(this.normalizeRoute(menu.route));
    }
  }
}
